import { useMemo } from 'react';

import {
  GET_ONBOARDING_V2_VARIANT,
  type OnboardingV2Variant,
} from '@/features/flags/legacy-flags';
import { OnboardingProvider } from '@/features/onboarding-v2/context/OnboardingProvider';
import {
  ClinicStep,
  PersonalizationLayout,
  useIsOnboardingExampleNoteGenerating,
  useOnboardingContext,
} from '@heidi/growth';
import type { OnboardingFlowKind } from '@heidi/growth/contracts';

import { LearnBasicsFlowSteps } from './LearnBasicsFlowSteps';
import {
  CompletionStep,
  DocumentSelectionStep,
  InviteTeamStep,
  MicPermissionsStep,
  NameStep,
  RoleStep,
  TemplateCreationStep,
  TemplateLoadingStep,
  TemplateReviewStep,
} from './steps';

/**
 * Both treatment arms render the learn-basics (sample-conversation) steps; the
 * guided tour (`experimental-3`) is a layout of that flow resolved by
 * `usePersonalizationForm`, not a separate step set. `control` is gated out
 * before this component mounts (`IS_ONBOARDING_V2_TREATMENT`) and keeps the
 * `v2` fallback it always had. No variant selects `v2` any more; that step set
 * is retired in a follow-up.
 */
function variantToFlowKind(variant: OnboardingV2Variant): OnboardingFlowKind {
  return variant === 'control' ? 'v2' : 'learn-basics';
}

interface PersonalizationFlowProps {
  onComplete: () => void;
  resumeAfterAuthOnboard?: boolean;
}

export function PersonalizationFlow({
  onComplete,
  resumeAfterAuthOnboard = false,
}: PersonalizationFlowProps) {
  // Resolve once so the flow can't flip mid-session on a PostHog reload.
  const flowKind = useMemo<OnboardingFlowKind>(
    () => variantToFlowKind(GET_ONBOARDING_V2_VARIANT()),
    []
  );

  return (
    <OnboardingProvider
      onComplete={onComplete}
      flowKind={flowKind}
      resumeAfterAuthOnboard={resumeAfterAuthOnboard}
    >
      {flowKind === 'learn-basics' ? <LearnBasicsFlowSteps /> : <V2FlowSteps />}
    </OnboardingProvider>
  );
}

function V2FlowSteps() {
  const ctx = useOnboardingContext();
  const isGeneratingExampleNote = useIsOnboardingExampleNoteGenerating();
  const {
    formData,
    updateFormData,
    currentStep,
    isCurrentStepValid,
    availableDisplayLanguages,
    showLanguageDropdown,
    handleCountryChange,
    handleContinue,
    userEmail,
    submitAndAdvance,
    onMicSkip,
    template,
    documents,
    completion,
  } = ctx;

  return (
    <PersonalizationLayout>
      {currentStep === 'name' && (
        <NameStep
          firstName={formData.firstName}
          lastName={formData.lastName}
          onFirstNameChange={(value) => updateFormData('firstName', value)}
          onLastNameChange={(value) => updateFormData('lastName', value)}
          onContinue={handleContinue}
          isValid={isCurrentStepValid}
        />
      )}
      {currentStep === 'role' && (
        <RoleStep
          specialty={formData.specialty}
          otherSpecialty={formData.otherSpecialty}
          role={formData.role}
          country={formData.country}
          jurisdiction={formData.jurisdiction}
          displayLanguage={formData.displayLanguage}
          availableDisplayLanguages={availableDisplayLanguages}
          showLanguageDropdown={showLanguageDropdown}
          phoneNumber={formData.phoneNumber}
          onSpecialtyChange={(value) => updateFormData('specialty', value)}
          onOtherSpecialtyChange={(value) => updateFormData('otherSpecialty', value)}
          onRoleChange={(value) => updateFormData('role', value)}
          onCountryChange={handleCountryChange}
          onJurisdictionChange={(value) => updateFormData('jurisdiction', value)}
          onDisplayLanguageChange={(v) => updateFormData('displayLanguage', v)}
          onPhoneNumberChange={(value) => updateFormData('phoneNumber', value)}
          onContinue={handleContinue}
          isValid={isCurrentStepValid}
        />
      )}
      {currentStep === 'clinic' && (
        <ClinicStep
          clinicName={formData.clinicName}
          country={formData.country}
          clinicSize={formData.clinicSize}
          emrEhr={formData.emrEhr}
          onClinicNameChange={(value) => updateFormData('clinicName', value)}
          onOfficialPracticeChange={(isOfficial, practiceId) => {
            updateFormData('isOfficialPractice', isOfficial);
            updateFormData('cqcPracticeId', practiceId);
          }}
          onClinicSizeChange={(value) => updateFormData('clinicSize', value)}
          onEmrEhrChange={(value) => updateFormData('emrEhr', value)}
          onContinue={handleContinue}
          isValid={isCurrentStepValid}
        />
      )}
      {currentStep === 'invite-team' && formData.clinicSize && <InviteTeamStep />}
      {currentStep === 'mic-permissions' && (
        <MicPermissionsStep onContinue={handleContinue} onSkip={onMicSkip} />
      )}
      {currentStep === 'template-creation' && (
        <TemplateCreationStep
          templateDescription={formData.templateDescription}
          specialty={formData.specialty}
          country={formData.country}
          onTemplateDescriptionChange={(v) => updateFormData('templateDescription', v)}
          onCreateTemplate={template.handleCreateTemplate}
          onSkip={template.handleSkipTemplate}
          isValid={isCurrentStepValid}
          isLoading={template.isGenerating}
        />
      )}
      {currentStep === 'template-loading' && <TemplateLoadingStep />}
      {currentStep === 'template-review' && (
        <TemplateReviewStep
          onSaveAndContinue={template.handleSaveAndContinue}
          onApplyChanges={template.handleApplyChanges}
          onUndoLastTemplateEdit={template.handleUndoLastTemplateEdit}
          canUndoLastTemplateEdit={template.canUndoLastTemplateEdit}
          onEditingChange={template.setIsEditingTemplate}
          isLoading={false}
          isApplying={template.isEditing}
          isGeneratingExample={isGeneratingExampleNote}
        />
      )}
      {currentStep === 'document-selection' && (
        <DocumentSelectionStep
          templates={documents.documentTemplates}
          selectedIds={documents.selectedDocumentIds}
          onToggle={documents.toggleDocumentSelection}
          onSkip={() => submitAndAdvance(true)}
          onContinue={() => submitAndAdvance(false)}
          isLoading={documents.isLoadingDocuments}
          isSubmitting={false}
        />
      )}
      {currentStep === 'completion' && (
        <CompletionStep
          firstName={formData.firstName}
          lastName={formData.lastName}
          email={userEmail}
          companyName={formData.clinicName || undefined}
          countryName={formData.country?.name}
          countryCode={formData.country?.countryCode}
          displayLanguage={formData.displayLanguage}
          ctaOptIn={formData.ctaOptIn}
          onCtaOptInChange={(value) => updateFormData('ctaOptIn', value)}
          demoReason={formData.demoReason}
          onDemoReasonChange={(value) => updateFormData('demoReason', value)}
          phoneNumber={formData.phoneNumber}
          onPhoneNumberChange={(value) => updateFormData('phoneNumber', value)}
          onGetStarted={completion.onGetStarted}
          isSubmitting={completion.isCompletingOnboarding}
          error={completion.onboardingError?.message}
        />
      )}
    </PersonalizationLayout>
  );
}
